import { useState } from 'react';
import MediaLightbox from './MediaLightbox';

interface ReviewCardProps {
  review: any;
}

export default function ReviewCard({ review }: ReviewCardProps) {
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showFullReview, setShowFullReview] = useState(false);

  const hasMedia = review.images?.length > 0 || review.video;
  const reviewText = review.review || review.text || '';
  const isLongReview = reviewText.length > 300;

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setShowLightbox(true);
  };

  return (
    <>
      <article
        aria-label={`Review by ${review.customer_name || review.name || 'Anonymous'}, ${review.rating} out of 5 stars`}
        className="bg-white rounded-2xl border border-[#E8E2D6] p-4 sm:p-6 hover:shadow-lg transition-shadow"
      >
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div className="flex items-start gap-3 sm:gap-4">
            {/* Avatar */}
            <div
              aria-hidden="true"
              className="w-10 h-10 sm:w-12 sm:h-12 bg-[#E8B84B] rounded-full flex items-center justify-center text-[#1A2332] font-bold text-base sm:text-lg flex-shrink-0"
            >
              {review.customer_name?.[0] || review.name?.[0] || 'A'}
            </div>

            {/* Info */}
            <div>
              <h3 className="font-['Oswald'] text-lg sm:text-xl font-bold text-[#1A2332]">
                {review.customer_name || review.name || 'Anonymous'}
              </h3>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {/* Stars */}
                <div
                  role="img"
                  aria-label={`${review.rating} out of 5 stars`}
                  className="flex gap-0.5"
                >
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      aria-hidden="true"
                      className={`${
                        i < review.rating ? 'ri-star-fill text-[#E8B84B]' : 'ri-star-line text-gray-300'
                      } text-base sm:text-lg`}
                    ></i>
                  ))}
                </div>
                <time
                  dateTime={new Date(review.date || review.created_at).toISOString()}
                  className="text-xs sm:text-sm text-[#4A5568]"
                >
                  {new Date(review.date || review.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </time>
              </div>
              {review.vehicle && (
                <p className="text-xs sm:text-sm text-[#374151] mt-1">
                  <i className="ri-car-line text-[#E8B84B] mr-1" aria-hidden="true"></i>
                  {review.vehicle}
                </p>
              )}
            </div>
          </div>

          {/* Verified Badge */}
          {review.verified && (
            <div className="flex items-center gap-1 px-3 py-1 bg-[#F7F5F0] text-[#1A2332] rounded-full text-xs font-medium whitespace-nowrap self-start">
              <i className="ri-checkbox-circle-fill text-[#E8B84B]" aria-hidden="true"></i>
              Verified Purchase
            </div>
          )}
        </div>

        {/* Review Text */}
        <div className="mb-4">
          <p id={`review-text-${review.id || review.customer_name?.replace(/\s+/g, '-')}`} className="text-[#374151] leading-relaxed text-sm sm:text-base">
            {isLongReview && !showFullReview
              ? `${reviewText.substring(0, 300)}...`
              : reviewText}
          </p>
          {isLongReview && (
            <button
              onClick={() => setShowFullReview(!showFullReview)}
              aria-expanded={showFullReview}
              aria-controls={`review-text-${review.id || review.customer_name?.replace(/\s+/g, '-')}`}
              className="text-[#7a5208] hover:text-[#1A2332] font-medium text-sm mt-2 inline-flex items-center gap-1 cursor-pointer"
            >
              {showFullReview ? (
                <>Show Less <i className="ri-arrow-up-s-line" aria-hidden="true"></i></>
              ) : (
                <>Read More <i className="ri-arrow-down-s-line" aria-hidden="true"></i></>
              )}
            </button>
          )}
        </div>

        {/* Media Gallery */}
        {hasMedia && (
          <ul className="flex gap-2 mb-4 overflow-x-auto pb-2 list-none p-0" aria-label="Review photos and videos">
            {review.images?.map((image: string, index: number) => (
              <li key={index} className="flex-shrink-0">
                <button
                  onClick={() => openLightbox(index)}
                  aria-label={`View photo ${index + 1} from ${review.customer_name || review.name || 'customer'}`}
                  className="relative w-20 h-20 sm:w-24 sm:h-24 block rounded-lg overflow-hidden border-2 border-[#E8E2D6] hover:border-[#E8B84B] transition-colors cursor-pointer"
                >
                  <img src={image} alt={`Review photo ${index + 1}`} className="w-full h-full object-cover" width={96} height={96} loading="lazy" decoding="async" />
                </button>
              </li>
            ))}
            {review.video && (
              <li className="flex-shrink-0">
                <button
                  onClick={() => openLightbox(review.images?.length || 0)}
                  aria-label="Play review video"
                  className="relative w-20 h-20 sm:w-24 sm:h-24 block rounded-lg overflow-hidden border-2 border-[#E8E2D6] hover:border-[#E8B84B] transition-colors bg-gray-900 cursor-pointer"
                >
                  <i className="ri-play-circle-fill text-white text-4xl absolute inset-0 flex items-center justify-center" aria-hidden="true"></i>
                </button>
              </li>
            )}
          </ul>
        )}

        {/* Response */}
        {review.response && (
          <aside
            aria-label="Dealer response"
            className="mt-4 p-3 sm:p-4 bg-[#F7F5F0] rounded-xl border border-[#E8E2D6]"
          >
            <div className="flex items-start gap-2 sm:gap-3">
              <div aria-hidden="true" className="w-8 h-8 sm:w-10 sm:h-10 bg-[#1A2332] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-sm">
                R
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-['Oswald'] font-bold text-[#1A2332] mb-1 text-sm sm:text-base">
                  Response from Rockhound Auto Sales
                </p>
                <p className="text-[#374151] text-xs sm:text-sm leading-relaxed">{review.response}</p>
              </div>
            </div>
          </aside>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 sm:gap-4 mt-4 pt-4 border-t border-[#E8E2D6]">
          <button
            aria-label={`Mark review by ${review.customer_name || review.name || 'customer'} as helpful`}
            className="text-[#374151] hover:text-[#7a5208] text-xs sm:text-sm font-medium inline-flex items-center gap-1 transition-colors cursor-pointer"
          >
            <i className="ri-thumb-up-line" aria-hidden="true"></i>
            Helpful ({review.helpful_count || 0})
          </button>
          <button
            aria-label={`Share review by ${review.customer_name || review.name || 'customer'}`}
            className="text-[#374151] hover:text-[#7a5208] text-xs sm:text-sm font-medium inline-flex items-center gap-1 transition-colors cursor-pointer"
          >
            <i className="ri-share-line" aria-hidden="true"></i>
            Share
          </button>
        </div>
      </article>

      {/* Lightbox */}
      {showLightbox && (
        <MediaLightbox
          images={review.images || []}
          video={review.video}
          initialIndex={lightboxIndex}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </>
  );
}